import React, { useEffect, useState, useContext, useCallback, useReducer, useRef } from 'react';
import { useLocation, useNavigate, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { makeStyles, ThemeProvider as MuiThemeProvider } from '@mui/styles';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine';
import CssBaseline from '@mui/material/CssBaseline';
import Link from '@mui/material/Link';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import MomentUtils from '@date-io/moment';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import Themes from '../../data/themes';

import SideNav from '../../components/sidenav/cmc';
import TopNav from '../../components/topnav';
import Breadcrumbs from '../../components/breadcrumbs';
import Notifications from '../../components/notifications';
import HeaderMessage from '../../components/messages/header';
import FooterMessage, { Copyright } from '../../components/messages/footer';
import FortyTwo from '../../components/fortytwo.js';

import NotifyService from '../../data/notify-service';
import AssetsService from '../../data/assets-service';
import TagsService from '../../data/tags-service';
import SensorsService from '../../data/sensors-service';
import IncidentService from '../../data/incident-service';
import SystemService from '../../data/system-service';
import BacktestingService from '../../data/backtesting-service';
import MetadataService, { DefaultMapping as DefaultMtdtMappings } from '../../data/metadata-service';
import AlertsService, { DefaultMapping as DefaultAlertMappings } from '../../data/alerts-service';
import Auth, { ProfileContext, UISettings, SupportSettings } from '../../data/auth-service';
import DataContext from '../../data/data-context';
import { UpdateReducer, UpdateAllReducer, IsSameMappings, TranslateTheme } from '../../libraries/helpers';
import { blue, neutrals1 } from '../../data/colors';

// import { useWhatChangedEffect } from '../../libraries/withwhyrender';

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        transition: 'margin-left 500ms',
    },

    // this only needs to be defined once in the app
    '@global': {
        '.react-codemirror2': {
            '& .cm-s-material': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: blue[900],
                padding: 5,
                fontSize: 15,
            },
            '& .cm-s-xq-light': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: neutrals1[0],
                padding: 5,
                fontSize: 15,
            },

            '& .CodeMirror.CodeMirror-focused': {
                border: `solid 1px ${theme.palette.primary.main}`,
            },
        },
    },
}));

const AppWrapper = ({ children }) => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Themes.DarkTheme}>
                <MuiThemeProvider theme={Themes.DarkTheme}>
                    <App />
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};
AppWrapper.propTypes = {
    children: PropTypes.node,
};

const setAlertMitre = AlertsService.setAlertMitre;
const setMtdtMitre = MetadataService.setMtdtMitre;
const clearAlertMitre = AlertsService.clearAlertMitre;
const clearMtdtMitre = MetadataService.clearMtdtMitre;
const setAlertsMitre = AlertsService.setAlertsMitre;
const setMtdtsMitre = MetadataService.setMtdtsMitre;
const clearAlertsMitre = AlertsService.clearAlertsMitre;
const clearMtdtsMitre = MetadataService.clearMtdtsMitre;

const App = (props) => {
    const classes = useStyles();
    const location = useLocation();

    const [systemSettings, setSystemSettings] = useReducer(UpdateReducer, { object: {} });
    const [systemHealth, setSystemHealth] = useReducer(UpdateAllReducer, { object: {} });

    const [offset, setOffset] = useState(135);
    const [messages, setMessages] = useState({
        header: '',
        footer: '',
    });

    // context common to many pages
    const [assets, setAssets] = useState({});
    const [tags, setTags] = useState({});
    const [sensorNames, setSensorNames] = useState({});
    const [mitreCategories, setMitreCategories] = useState([]);
    const [mitreCategoriesMap, setMitreCategoriesMap] = useState({});
    const [lookups, setLookups] = useState([]);
    const [filehashLookups, setFilehashLookups] = useState([]);
    // use backtestingJobsLite when you just need to know about job name,uuid,desc updates
    const [backtestingJobsLite, setBacktestingJobsLite] = useReducer(UpdateAllReducer, {});
    // backtestingJobs includes dates which change while the jobs is running
    const [backtestingJobs, setBacktestingJobs] = useReducer(UpdateAllReducer, {});

    const [showIP, setShowIP] = useState(false);
    const [showTag, setShowTag] = useState(false);
    const [theme, setTheme] = useState(Themes.DarkTheme);
    const [fortyTwo, setFortyTwo] = useState(false);

    const { updateProfile, getProfile } = useContext(ProfileContext);

    const [sidenav, _setSidenav] = useReducer(UpdateReducer, { collapsed: false, panels: {} });

    const alertsPerms = Auth.permissions.get('Alerts');
    const btPerms = Auth.permissions.get('Backtesting');
    const sensorPerms = Auth.permissions.get('Sensors');
    const assetPerms = Auth.permissions.get('Assets');
    const tagPerms = Auth.permissions.get('Tags');
    const healthPerms = Auth.permissions.get('SystemHealth');

    const setSidenav = useCallback((s, m) => {
        updateProfile('nav', { ...s, ...m });
        _setSidenav({ ...s, ...m });
    }, [updateProfile, _setSidenav]);

    // mapping dispatchers, update service, stash in profile and finally return state
    const setAndStashAlertMappings = useCallback((s, m) => {
        if (m && IsSameMappings(s, m)) return s;
        const mappings = AlertsService.doColumnMapping(m);
        const profile = getProfile('alerts');
        if (!IsSameMappings(mappings, (profile.mappings || {}))) {
            updateProfile('alerts', { ...profile, mappings });
        }
        return { ...mappings };
    }, [getProfile, updateProfile]);

    const setAndStashMetadataMappings = useCallback((s, m) => {
        if (m && IsSameMappings(s, m)) return s;
        const mappings = MetadataService.doColumnMapping(m);
        const profile = getProfile('metadata');
        if (!IsSameMappings(mappings, (profile.mappings || {}))) {
            updateProfile('metadata', { ...profile, mappings });
        }
        return { ...mappings };
    }, [getProfile, updateProfile]);

    const [alertMappings, setAlertMappings] = useReducer(setAndStashAlertMappings, DefaultAlertMappings);
    const [mtdtMappings, setMtdtMappings] = useReducer(setAndStashMetadataMappings, DefaultMtdtMappings);

    // Don't know where this went but it's needed
    useEffect(() => {
        SystemService.getSystemSettings().then((res) => {
            setSystemSettings(res);
        });
    }, []);

    const updateShowIP = useCallback(showip => {
        const profile = getProfile('iptool');
        updateProfile('iptool', { ...profile, showip });
        setShowIP(showip);
    }, [getProfile, updateProfile]);

    const updateAsset = useCallback(({ orig, ip, name, data }) => {
        setAssets(current => {
            delete current[orig];
            return { ...current, [ip]: { ip, name, data } };
        });
        return AssetsService.updateAsset(orig || ip, { ip, name, data });
    }, []);

    const createAsset = useCallback(({ ip, name }) => {
        setAssets(current => {
            return { ...current, [ip]: { ip, name, data: {} } };
        });
        return AssetsService.createAsset({ ip, name, data: {} });
    }, []);

    const deleteAsset = useCallback(ip => {
        setAssets(current => {
            delete current[ip];
            return current;
        });
        return AssetsService.deleteAsset(ip);
    }, []);

    // updates from context (deleted in system/assets)
    // throttle, potentially very long list
    const refreshAssets = useCallback(async () => {
        assetPerms.read && AssetsService.getAssetsMap().then(a => {
            setAssets(a.data || {});
        });
    }, []);

    const fetchTags = useCallback(async () => {
        tagPerms.read && TagsService.list().then(t => setTags(t));
    }, []);

    const upsertTag = useCallback(async tag => {
        tagPerms.write && TagsService.upsert(tag).then(fetchTags);
    }, [fetchTags]);

    const deleteTag = useCallback(async tag => {
        tagPerms.delete && TagsService.delete(tag).then(fetchTags);
    }, [fetchTags]);

    const updateShowTag = useCallback(showtag => {
        const profile = getProfile('tagtool');
        updateProfile('tagtool', { ...profile, showtag });
        setShowTag(showtag);
    }, [getProfile, updateProfile]);

    const refreshSensorNames = useCallback(async refresh => {
        sensorPerms?.read && SensorsService.getNames(refresh).then(names => {
            setSensorNames(names);
        });
    }, []);

    const cancelBacktesting = useRef(null);
    const refreshBacktestingJobs = useCallback(async () => {
        const fetch = () => {
            if (!(alertsPerms.read && btPerms.read)) {
                setBacktestingJobsLite({});
                setBacktestingJobs({});
                return;
            }
            BacktestingService.getJobs().then(res => {
                // setBacktestingJobsLite for when you don't need start/end time which are updated as the job runs
                setBacktestingJobsLite((res || []).reduce((a, c) => ({
                    ...a,
                    [c.uuid]: {
                        uuid: c.uuid,
                        name: c.name,
                        description: c.descritpion,
                        sensor: c.sensor,
                        created: c.created,
                    }
                }), {}));
                setBacktestingJobs((res || []).reduce((a, c) => ({
                    ...a,
                    [c.uuid]: {
                        uuid: c.uuid,
                        name: c.name,
                        description: c.descritpion,
                        sensor: c.sensor,
                        // rested adds a 10 minute buffer to these dates
                        // to reduce frequent updates
                        start: c.data.start,
                        end: c.data.end,
                        created: c.created,
                    }
                }), {}));
                // tee up the next refresh
                const rate = (res || []).some(b =>
                    b.status === 'pushed' ||
                    b.status === 'running' ||
                    b.status === 'queued') ? 11000 : 42000;
                clearTimeout(cancelBacktesting.current);
                cancelBacktesting.current = setTimeout(fetch, rate);
            });
        };

        clearTimeout(cancelBacktesting.current);
        fetch();
    }, []);

    const changeTheme = useCallback(themename => {
        let t;
        switch (themename) {
            case 'light':
                t = Themes.LightTheme;
                break;
            case 'pink':
                t = Themes.PinkTheme;
                break;
            default:
                t = Themes.DarkTheme;
        }
        setTheme(current => {
            return { ...t, date: { ...current.date, ...getProfile('date') } };
        });

        // update cookie
        Auth.updateTheme(themename);
    }, [getProfile]);

    const changeDateFormat = useCallback(formats => {
        // const date = { ...theme.date, ...formats };
        updateProfile('date', formats);
        setTheme(current => {
            return { ...current, date: { ...current.date, ...formats } };
        });

        // update cookie
        Auth.updateDates(formats);
    }, [updateProfile]);

    const updateSystemSettings = useCallback((data, customToast) => {
        SystemService.updateSystemSettings(data).then(() => {
            setSystemSettings(data);
            if (customToast) {
                toast.success(customToast);
            } else {
                toast.success('System updated successfully.');
            }
        }, error => {
            console.log(error);
            toast.error('Failed to update System settings...');
        });
    }, []);

    useEffect(() => {
        let stopNotification;
        let retry = 0;
        if (healthPerms.read) {
            NotifyService.getNotifications().then((res) => {
                // defense against the dark arts of permissions
                setSystemHealth({ ...res, object: res.object || {} });
                stopNotification = setInterval(() => {
                    if (retry >= 3) {
                        console.log('too many attempts to get health, service may be down');
                        stopNotification && clearInterval(stopNotification);
                        Auth.boot();
                    }
                    NotifyService.getNotifications().then((res) => {
                        if (!res) return;
                        retry = 0;
                        setSystemHealth(res);
                    }, (err) => {
                        console.error('system health error', err);
                        retry++;
                    });
                }, NotifyService.HEALTH_POLL_INTERVAL);
            }, (err) => {
                console.error('cannot get system health information', err);
                Auth.boot();
            });
        }

        // load context for app
        // assets
        refreshAssets();

        // fetch ui
        (async () => {
            UISettings.fetchUI(true).then(data => {
                if (!data) return;
                setMessages(data.messages || {});
                setSystemSettings({ ui: data });
            });
        })();

        // sensorNames
        refreshSensorNames();

        // bbacktesting job
        refreshBacktestingJobs();

        // tags
        fetchTags();

        // mitre
        (async () => {
            IncidentService.getMitreCategories().then(a => {
                setMitreCategories(a);
            });
        })();
        (async () => {
            IncidentService.getMitreCategoriesMap().then(a => {
                setMitreCategoriesMap(a);
            });
        })();

        // column mapping
        setAlertMappings((getProfile('alerts') || {}).mappings);
        setMtdtMappings((getProfile('metadata') || {}).mappings);

        const iptoolProfile = getProfile('iptool');
        setShowIP(!!iptoolProfile.showip);

        const tagtoolProfile = getProfile('tagtool');
        setShowTag(!!tagtoolProfile.showtag);

        const themename = TranslateTheme(getProfile('colorscheme')) || 'dark';
        const date = getProfile('date');
        let datheme;
        switch (themename) {
            case 'light':
                datheme = Themes.LightTheme;
                break;
            case 'pink':
                datheme = Themes.PinkTheme;
                break;
            default:
                datheme = Themes.DarkTheme;
        }
        setTheme({ ...datheme, date: { ...datheme.date, ...date } });

        // no need to stash this update to profile
        _setSidenav(getProfile('nav'));

        // Konami Code, shhhhh....
        (() => {
            const s = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a', 'Enter'];
            const ss = ['t', 'h', 'e', 'a', 'n', 's', 'w', 'e', 'r', 'i', 's', 'f', 'o', 'r', 't', 'y', 't', 'w', 'o'];
            let cs = [];
            const tf = () => { cs = [] };
            let t = setTimeout(tf, 1500);

            window.addEventListener('keydown', e => {
                clearTimeout(t);
                cs.push(e.key);
                if (e.key === 'Escape') setFortyTwo(false);

                const eq = s.length === cs.length && s.every((v, i) => v === cs[i]);
                if (eq) {
                    toast.dark('You found a secret! Check your themes...');
                    updateProfile('secret_theme', true);
                }
                const eqq = ss.length === cs.length && ss.every((v, i) => v === cs[i]);
                if (eqq) setFortyTwo(true);

                t = setTimeout(tf, 1500);
            });
        })();

        return () => {
            console.log('Thank You Come Again');
            stopNotification && clearInterval(stopNotification);
            cancelBacktesting && clearTimeout(cancelBacktesting.current);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateProfile, getProfile, fetchTags, refreshBacktestingJobs, refreshAssets]);

    useEffect(() => {
        const sn = Object.keys(sensorNames);
        if (sn.length) {
            SupportSettings.fetchSupport().then(supportData => {
                const bt = sn.reduce((a, c) => {
                    if (!a) {
                        return sensorNames[c].backtesting;
                    }
                    return true;
                }, false);
                if (!supportData.exists && bt) {
                    updateSystemSettings({
                        support: {
                            backtesting: {
                                exists: bt,
                                ui: bt,
                            }
                        }
                    }, 'Backtesting has been enabled');
                } else {
                    setSystemSettings({
                        ...systemSettings,
                        support: {
                            backtesting: {
                                ...supportData
                            }
                        }
                    });
                }
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sensorNames]);

    // sidenav.sidebar has three states - undefined, 'open' or 'collapsed'
    useEffect(() => {
        let o = 60;
        if (!sidenav.collapsed) o = 300;
        if (sidenav.sidebar === 'open') o += 300;
        else if (sidenav.sidebar === 'collapsed') o += 60;
        setOffset(o);
    }, [sidenav]);

    // these are for convenience (and backward compatibility)
    // they are also gettable from the generic systemSettings context object.
    useEffect(() => {
        const settings = { ui: {}, ...systemSettings };
        setMessages(settings.ui.messages || {});
        setLookups((settings.ui.lookups || []).filter(d => d.url));
        setFilehashLookups((settings.ui.file_lookups || []).filter(d => d.url));
    }, [systemSettings]);

    useEffect(() => {
        if (!systemHealth) return;
        (systemHealth.notices || []).forEach(notice => {
            switch (notice.severity) {
                case 'info':
                    toast.info(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: 11000 });
                    break;
                case 'success':
                    toast.success(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: 11000 });
                    break;
                case 'warn':
                    toast.warn(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: false });
                    break;
                case 'critical':
                    toast.error(<NoticeToast notice={notice}/>,
                        { toastId: notice.uuid, autoClose: false });
                    break;
            }
        });
    }, [systemHealth], true);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <MuiThemeProvider theme={theme}>
                    <ThemeChanger.Provider value={{ changeTheme, changeDateFormat }}>
                        {/* <MuiPickersUtilsProvider utils={MomentUtils}> */}
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            {/* one data context to rule them all */}
                            <DataContext.Provider value={{
                                assets,
                                updateAsset,
                                createAsset,
                                deleteAsset,
                                refreshAssets,
                                showIP,
                                updateShowIP,
                                lookups,
                                filehashLookups,
                                tags,
                                upsertTag,
                                deleteTag,
                                showTag,
                                updateShowTag,
                                mitreCategories,
                                mitreCategoriesMap,
                                setAlertMitre,
                                setMtdtMitre,
                                clearAlertMitre,
                                clearMtdtMitre,
                                setAlertsMitre,
                                setMtdtsMitre,
                                clearAlertsMitre,
                                clearMtdtsMitre,
                                sensorNames,
                                refreshSensorNames,
                                backtestingJobsLite,
                                backtestingJobs,
                                refreshBacktestingJobs,
                                alertMappings,
                                setAlertMappings,
                                mtdtMappings,
                                setMtdtMappings,
                                systemHealth,
                                setSystemHealth,
                                systemSettings,
                                setSystemSettings,
                                updateSystemSettings,
                                sidenav,
                                setSidenav,
                            }}>
                                <React.Fragment>
                                    <CssBaseline />
                                    <Notifications />
                                    {messages.header && <HeaderMessage />}
                                    {messages.footer && <FooterMessage />}
                                    <Copyright />
                                    <TopNav header={Boolean(messages.header)} />
                                    {!location.pathname.match('eula') &&
                                    <SideNav header={Boolean(messages.header)} path={location.pathname}/>}
                                    {!location.pathname.match('eula') &&
                                    <Breadcrumbs
                                        collapsed={sidenav.collapsed && sidenav.sidebar !== 'open'}
                                        offset={offset}
                                        path={location.pathname}
                                        header={Boolean(messages.header)}
                                    />}
                                    <main className={classes.content} style={{
                                        marginLeft: (!location.pathname.match('eula') && offset),
                                    }}>
                                        <Outlet />
                                    </main>
                                </React.Fragment>
                            </DataContext.Provider>
                        </LocalizationProvider>
                        {/* </MuiPickersUtilsProvider> */}
                    </ThemeChanger.Provider>
                    {fortyTwo && <FortyTwo />}
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

App.propTypes = {
    children: PropTypes.node,
    location: PropTypes.object,
};

const NoticeToast = ({ closeToast, notice }) => {
    const classes = useStyles();
    const navigate = useNavigate();

    // todo: add more links here
    let link = notice.msg === 'Your PCAP is ready for download'
        ? {
            title: 'View pcaps',
            url: '/views/packet_requests',
        } : notice.uuid === 'ALERT_TAGS'
            ? {
                title: 'View Alerts',
                url: '/views/alerts',
            } : notice.uuid === 'ALERT_MITRE'
                ? {
                    title: 'View Alerts',
                    url: '/views/alerts',
                } : undefined;

    // all kind of harcoded magic
    const ss = (notice.uuid || '').split(':');
    if (ss.length > 1 && ss[0] === 'policy') {
        let translation = {};
        switch (ss[1]) {
            case 'btconf':
                translation = { link: 'bt_conf', group: 'sensor' };
                break;
            case 'data_tags':
                translation = { link: 'data_tuning', group: 'sensor' };
                break;
            case 'eexportd_conf':
                translation = { link: 'exports', group: 'sensor' };
                break;
            case 'file_hash':
                translation = { link: 'file_hash', group: 'sensor' };
                break;
            case 'custom_geoip':
                translation = { link: 'geo_ip', group: 'sensor' };
                break;
            case 'pcap_config':
                translation = { link: 'pcap', group: 'sensor' };
                break;
            case 'system_config':
                translation = { link: 'configuration', group: 'sensor' };
                break;
            case 'sensor_upgrade':
                translation = { link: 'upgrade', group: 'sensor' };
                break;
            case 'suricata_config':
                translation = { link: 'vars', group: 'suricata' };
                break;
            case 'suricata_rules':
                translation = { link: 'rules', group: 'suricata' };
                break;
            case 'bt_suricata_manual':
                translation = { link: 'bt_rules', group: 'suricata' };
                break;
            case 'bt_bro_ti':
                translation = { link: 'bt_scripts', group: 'zeek' };
                break;
            case 'bro_ti':
                translation = { link: 'scripts', group: 'zeek' };
                break;
        }
        link = {
            title: 'Rename policy',
            url: `/policies/${translation.group}/${translation.link}`,
        };
    }

    const handleLink = url => {
        toast.dismiss(notice.uuid);
        navigate(url);
    };

    return (
        <div className={classes.root}>
            {notice.msg}
            {link &&
                <div>
                    <Link
                        style={{ marginLeft: 10 }}
                        aria-label="notice link"
                        onClick={ () => handleLink(link.url) }>
                        {link.title}
                    </Link>
                </div>
            }
        </div>
    );
};
NoticeToast.propTypes = {
    toastProps: PropTypes.object,
    closeToast: PropTypes.func,
    notice: PropTypes.shape({
        acks: PropTypes.object,
        msg: PropTypes.string,
        recipients: PropTypes.string,
        severity: PropTypes.string,
        timestamp: PropTypes.string,
        ttl: PropTypes.number,
        uuid: PropTypes.string,
    }),
};

const Updates = ({ updates }) => (
    <h4>Renders<br />{updates}</h4>
);
Updates.propTypes = {
    updates: PropTypes.object
};

const WhyUpdate = React.memo(({ children }) => {
    const updates = React.useRef(0);
    return (
        <div style={{ color: 'white', textAlign: 'center' }}>
            <Updates updates={updates.current++} />
            {children}
        </div>
    );
});
WhyUpdate.propTypes = {
    children: PropTypes.node
};

const ThemeChanger = React.createContext({
    changeTheme: theme => {},
    changeDateFormat: formats => {},
});

export {
    App as default,
    AppWrapper,
    ThemeChanger,
    NoticeToast,
};
